import { useNav } from '../app/ui';
import { useMemo, useState } from 'react';
import { addDays, hhmm, normalizeDesign, ymd, zonedToInstant } from '@noorview/core';
import { attempt, backend, toast, useApp } from '../data/store';
import { Row, Sheet, Switch } from '../ui/controls';
import { DateInput, Select, TimeInput } from '../ui/pickers';
import { Icon } from '../ui/icons';

const DURATIONS = [[15, '15 minutes'], [30, '30 minutes'], [60, '1 hour'], [90, '1½ hours'], [120, '2 hours'], [180, '3 hours'], [360, '6 hours'], [720, '12 hours'], [1440, '1 day'], [2880, '2 days'], [4320, '3 days'], [10080, '7 days']] as const;
const durationLabel = (m: number) => DURATIONS.find(([v]) => v === m)?.[1] ?? (m % 60 ? `${m} minutes` : `${m / 60} hours`);

/**
 * Schedule: show one of the masjid's saved templates on a screen from a start time for a set length, optionally
 * every week. The template is what appears; make it in the Studio and save it under My templates first.
 */
export function ScheduleSheet({ screenId, onClose }: { screenId?: string; onClose: () => void }) {
  const ws = useApp((s) => s.ws)!;
  const nav = useNav();
  const tz = ws.masjid.timezone;
  const now = new Date();
  const templates = useMemo(() => [...(ws.templates ?? [])].reverse(), [ws.templates]);
  // Locked screens can't be scheduled (lock means lockdown), so start on the first unlocked one.
  const [screen, setScreen] = useState(screenId ?? ws.screens.find((s) => !s.locked)?.id ?? ws.screens[0]?.id ?? '');
  const [content, setContent] = useState(templates[0]?.id ?? '');
  const [date, setDate] = useState(ymd(now, tz));
  const [time, setTime] = useState(() => { const t = hhmm(new Date(now.getTime() + 60 * 60_000), tz); return `${t.slice(0, 2)}:00`; });
  const [duration, setDuration] = useState(60);
  const [repeat, setRepeat] = useState(false);
  const [busy, setBusy] = useState(false);
  const existing = ws.schedules.filter((s) => s.screenId === screen).sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const scr = ws.screens.find((s) => s.id === screen);
  const held = !!scr?.locked;
  const screenAspect = ws.designs.find((d) => d.id === scr?.designId)?.draft.aspect ?? '16:9';
  const tpl = templates.find((t) => t.id === content);

  const startsAt = zonedToInstant(date, time, tz);
  const endsAt = new Date(startsAt.getTime() + duration * 60_000);
  const fmt = (d: Date) => d.toLocaleString('en-GB', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const endLabel = ymd(endsAt, tz) === date ? hhmm(endsAt, tz) : fmt(endsAt);
  const past = startsAt.getTime() < now.getTime() - 60_000;
  const clash = existing.find((s) => { const a = Date.parse(s.startsAt), b = a + s.durationMinutes * 60_000; return !s.repeatWeekly && a < endsAt.getTime() && startsAt.getTime() < b; });

  const toStudio = () => { onClose(); nav(`/studio/${screen}`); toast('Design what should show, then save it from Templates › My templates.'); };

  const submit = async () => {
    if (!screen || !tpl) return;
    setBusy(true);
    // The schedule shows its own published copy of the template, so later edits to the template or the screen's
    // design don't change what was scheduled.
    const r = await attempt(async () => {
      const rec = await backend.createDesign(`${tpl.name} (scheduled)`, normalizeDesign(structuredClone(tpl.design)));
      await backend.publish(rec.id);
      await backend.upsertSchedule({ screenId: screen, designId: rec.id, title: tpl.name, startsAt: startsAt.toISOString(), durationMinutes: duration, repeatWeekly: repeat });
      return true;
    }, `${tpl.name} scheduled on ${scr?.name ?? 'the screen'}`);
    setBusy(false);
    if (r) onClose();
  };

  return (
    <Sheet open onClose={onClose} title="Schedule" subtitle="Show one of your templates on a screen at a set time"
      footer={<><button type="button" className="btn plain" onClick={onClose}>Cancel</button><button type="button" className="btn primary" disabled={busy || !tpl || !screen || past || held} onClick={submit}>{busy ? 'Scheduling…' : 'Schedule'}</button></>}>
      <div className="group">
        <Row icon="monitor" label="Screen"><Select variant="inline" label="Screen" value={screen} onChange={setScreen} options={ws.screens.map((s) => ({ value: s.id, label: s.name, ...(s.locked ? { hint: 'Locked', disabled: s.id !== screen } : {}) }))} /></Row>
        <Row icon="layers" label="Template">
          {templates.length
            ? <Select variant="inline" label="Template" value={content} onChange={setContent} options={templates.map((t) => ({ value: t.id, label: t.design.aspect === screenAspect ? t.name : `${t.name} (${t.design.aspect})` }))} />
            : <span className="hint">None saved yet</span>}
        </Row>
        <Row icon="calendar" label="Date"><DateInput variant="inline" label="Date" value={date} min={ymd(now, tz)} max={addDays(ymd(now, tz), 730)} onChange={setDate} /></Row>
        <Row icon="clock" label="Starts"><TimeInput variant="inline" label="Start time" value={time} onChange={setTime} /></Row>
        <Row icon="hourglass" label="For"><Select variant="inline" label="Duration" value={String(duration)} onChange={(v) => setDuration(Number(v))} options={DURATIONS.map(([v, l]) => ({ value: String(v), label: l }))} /></Row>
        <Row icon="schedule" label="Ends"><span className="mono sched-end">{endLabel}</span></Row>
      </div>
      <div className="group"><div className="gi"><span className="ic"><Icon name="repeat" size={20} /></span><span className="k">Repeat weekly</span><span className="v"><Switch label="Repeat weekly" checked={repeat} onChange={setRepeat} /></span></div></div>
      {held && <div className="banner"><Icon name="lock" size={18} /><span style={{ flex: 1 }}>{scr!.name} is locked, so its schedules can’t be changed. Unlock it on the Screens page first.</span></div>}
      {!templates.length && <div className="banner"><span>Make what you want to show in the Studio, then save it under <b>My templates</b>. It will appear here to schedule.</span></div>}
      {tpl && tpl.design.aspect !== screenAspect && <div className="banner">“{tpl.name}” was made for {tpl.design.aspect} screens and {scr?.name ?? 'this screen'} is {screenAspect}, so it will show with bars at the sides.</div>}
      {past && <div className="banner bad">That start time has already passed.</div>}
      {clash && <div className="banner">This overlaps “{clash.title}”. The one that starts later shows while both are on.</div>}
      <button type="button" className="chip-btn" style={{ alignSelf: 'flex-start' }} onClick={toStudio}><Icon name="studio" size={18} />Make a new template in the Studio</button>
      {existing.length > 0 && (
        <div className="stack">
          <div className="section-label">Already scheduled on this screen</div>
          {existing.map((s) => {
            const a = new Date(s.startsAt), b = new Date(a.getTime() + s.durationMinutes * 60_000);
            return (
              <div key={s.id} className="row sched ctl">
                <Icon name="schedule" size={18} />
                <span style={{ flex: 1, minWidth: 0 }}><b>{s.title}</b><small className="hint" style={{ display: 'block' }}>{fmt(a)} to {ymd(b, tz) === ymd(a, tz) ? hhmm(b, tz) : fmt(b)} · {durationLabel(s.durationMinutes)}{s.repeatWeekly ? ' · Every week' : ''}</small></span>
                <button type="button" className="lbtn danger" aria-label={`Remove ${s.title}`} disabled={held} onClick={() => attempt(() => backend.deleteSchedule(s.id), 'Schedule removed')}><Icon name="trash" size={16} /></button>
              </div>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}
